import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw, Truck, Phone, Mail, Search, Download, Trash2, FileText } from "lucide-react";
import { LOGO } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["new", "contacted", "hired", "rejected"];
const statusColor = {
  new: "text-primary border-primary/40",
  contacted: "text-yellow-400 border-yellow-400/40",
  hired: "text-emerald-400 border-emerald-400/40",
  rejected: "text-red-400 border-red-400/40",
};

export default function Admin() {
  const [password, setPassword] = useState(sessionStorage.getItem("nl_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async (pw) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/applications`, { headers: { "x-admin-password": pw } });
      setApps(res.data);
      setAuthed(true);
      setPassword(pw);
      sessionStorage.setItem("nl_admin", pw);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Onjuist wachtwoord");
        setAuthed(false);
        sessionStorage.removeItem("nl_admin");
      } else {
        toast.error("Er ging iets mis");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (password) load(password);
  }, [password, load]);

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/applications/${id}?status=${status}`, {}, { headers: { "x-admin-password": password } });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch {
      toast.error("Kon status niet bijwerken");
    }
  };

  const removeApp = async (id) => {
    if (!window.confirm("Deze sollicitatie verwijderen?")) return;
    try {
      await axios.delete(`${API}/admin/applications/${id}`, { headers: { "x-admin-password": password } });
      setApps((prev) => prev.filter((a) => a.id !== id));
      toast.success("Verwijderd");
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const exportCsv = () => {
    const headers = ["Naam", "Telefoon", "E-mail", "Rijbewijs", "Ervaring", "Certificaten", "Beschikbaar", "Bericht", "Status", "Datum"];
    const rows = filtered.map((a) => [a.name, a.phone, a.email, a.license_type, a.experience, (a.certificates || []).join("; "), a.availability, (a.message || "").replace(/\n/g, " "), a.status, new Date(a.created_at).toLocaleString("nl-NL")]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `sollicitaties-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filtered = apps.filter((a) => {
    const q = query.toLowerCase();
    const match = !q || [a.name, a.email, a.phone, a.license_type, (a.certificates || []).join(" ")].join(" ").toLowerCase().includes(q);
    return match && (filter === "all" || a.status === filter);
  });

  if (!authed) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
        <form onSubmit={(e) => { e.preventDefault(); load(input); }} className="w-full max-w-sm border border-border bg-card p-8" data-testid="admin-login">
          <img src={LOGO} alt="Northline" className="h-14 w-auto mx-auto mb-6" />
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-3">
            <Lock className="w-4 h-4" /> Admin login
          </div>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Wachtwoord"
            className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-primary"
            data-testid="admin-password"
          />
          <button type="submit" disabled={loading} className="mt-4 w-full bg-primary text-primary-foreground py-3 text-sm font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors disabled:opacity-60" data-testid="admin-login-btn">
            {loading ? "..." : "Inloggen"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Northline" className="h-10 w-auto" />
            <span className="hidden sm:block h-7 w-px bg-border" />
            <span className="hidden sm:inline font-anton text-xl uppercase tracking-wide text-foreground leading-none">Sollicitaties</span>
          </div>
          <button onClick={() => load(password)} className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-widest hover:border-primary hover:text-primary transition-colors" data-testid="admin-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Ververs
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-10">
          {STATUSES.map((s) => (
            <div key={s} className="bg-card p-5">
              <div className="font-anton text-4xl text-primary">{apps.filter((a) => a.status === s).length}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Zoek op naam, e-mail, telefoon, rijbewijs..."
              className="w-full bg-card border border-border pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-primary"
              data-testid="admin-search"
            />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-card border border-border px-4 py-2.5 text-xs uppercase tracking-widest focus:outline-none" data-testid="admin-filter">
            <option value="all">Alle statussen</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={exportCsv} className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors" data-testid="admin-export">
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>

        {apps.length === 0 ? (
          <div className="border border-border p-16 text-center text-muted-foreground">
            <Truck className="w-10 h-10 mx-auto mb-4 opacity-50" />
            Nog geen sollicitaties.
          </div>
        ) : (
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="p-4">Naam</th>
                  <th className="p-4">Contact</th>
                  <th className="p-4">Rijbewijs</th>
                  <th className="p-4">Certificaten</th>
                  <th className="p-4">CV</th>
                  <th className="p-4">Ervaring</th>
                  <th className="p-4">Beschikbaar</th>
                  <th className="p-4">Datum</th>
                  <th className="p-4">Status</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-b border-border last:border-0 hover:bg-card transition-colors align-top" data-testid={`admin-row-${a.id}`}>
                    <td className="p-4 font-semibold">
                      {a.name}
                      {a.message && <div className="text-xs text-muted-foreground mt-1 max-w-[200px]">{a.message}</div>}
                    </td>
                    <td className="p-4 text-xs">
                      <a href={`tel:${a.phone}`} className="flex items-center gap-1.5 hover:text-primary"><Phone className="w-3 h-3" />{a.phone}</a>
                      <a href={`mailto:${a.email}`} className="flex items-center gap-1.5 hover:text-primary mt-1"><Mail className="w-3 h-3" />{a.email}</a>
                    </td>
                    <td className="p-4">{a.license_type}</td>
                    <td className="p-4">
                      {a.certificates && a.certificates.length > 0 ? (
                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                          {a.certificates.map((c) => (
                            <span key={c} className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary border border-primary/30 px-1.5 py-0.5">{c}</span>
                          ))}
                        </div>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-4">
                      {a.cv_path ? (
                        <a href={`${API}/files/${a.cv_path}?pw=${encodeURIComponent(password)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-xs" data-testid={`cv-link-${a.id}`}>
                          <FileText className="w-4 h-4" /> {a.cv_filename || "CV"}
                        </a>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-4">{a.experience}</td>
                    <td className="p-4">{a.availability || "—"}</td>
                    <td className="p-4 text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString("nl-NL")}</td>
                    <td className="p-4">
                      <select
                        value={a.status}
                        onChange={(e) => changeStatus(a.id, e.target.value)}
                        className={`bg-background border px-2 py-1.5 text-xs uppercase tracking-wider focus:outline-none ${statusColor[a.status] || ""}`}
                        data-testid={`status-select-${a.id}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s} className="text-foreground bg-background">{s}</option>)}
                      </select>
                    </td>
                    <td className="p-4">
                      <button onClick={() => removeApp(a.id)} className="text-muted-foreground hover:text-red-500 transition-colors" data-testid={`delete-${a.id}`} aria-label="Verwijderen">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
